import React, {
    useState, useEffect, useRef, useMemo,
    useImperativeHandle, forwardRef,
} from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import SignatureInput from './SignatureInput';
import { FormData, NomineeData, ValidationErrors, Form2Props, FormValidateHandle } from './typesIN';

const removeSpecialChars = (v: string) => v.replace(/[!#$%^&*()\-_=+;:'"<>/|\\{}[\]~`]/g, '');
const allowOnlyAlphabets = (v: string) => v.replace(/[^a-zA-Z\s.]/g, ''); // allow dot for initials e.g. "A. Kumar"
const ADDRESS_ALLOWED = /[^a-zA-Z0-9\s\-/#():,.&@]/g;
const sanitiseAddress = (v: string): string =>
    v.replace(ADDRESS_ALLOWED, '').replace(/-{2,}/g, '-').replace(/\/\*/g, '/').replace(/\*\//g, '/').slice(0, 200);

const toDateOrUndefined = (v: any): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? undefined : d;
};

const calculateAge = (dob: any): number | null => {
    const d = toDateOrUndefined(dob);
    if (!d) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
};

const addrRemaining = (v: string | undefined | null): string => {
    const len = (v || '').length;
    const remaining = 200 - len;
    if (remaining <= 20) return `${remaining} characters left`;
    return `${len}/200`;
};

const TODAY = new Date();

// ── Reusable Calendar with × clear button ────────────────────────────────────
const ClearableCalendar = ({
    value, onChange, className, ...rest
}: {
    value: Date | undefined;
    onChange: (val: Date | null) => void;
    className?: string;
    [key: string]: any;
}) => (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '100%' }}>
        <Calendar
            value={value}
            onChange={(e) => onChange(e.value as Date | null)}
            className={className}
            style={{ width: '100%' }}
            {...rest}
        />
        {value && (
            <i
                className="pi pi-times"
                onClick={() => onChange(null)}
                style={{ position: 'absolute', right: '36px', cursor: 'pointer', color: '#6c757d', fontSize: '0.75rem', zIndex: 1 }}
            />
        )}
    </div>
);

// ── Age validation helper ─────────────────────────────────────────────────────
const ageCheck = (v: unknown, label: string): string => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const n = Number(s);
    if (isNaN(n) || n < 1 || n > 120) return `${label} must be between 1 and 120`;
    return '';
};

const Form2 = forwardRef<FormValidateHandle, Form2Props>(
    ({ onContinue, onDraftSave, initialData = {}, initialNominees = [] }, ref) => {
        const toast = useRef<Toast>(null);

        const [formData, setFormData] = useState<FormData>({
            oracleId: localStorage.getItem('digitalFormOracleId') || '',
            fullName: localStorage.getItem('digitalFormfullName') || '',
            TotalAmount: '100',
            ...initialData,
        });

        useEffect(() => {
            if (initialData && Object.keys(initialData).length > 0) {
                setFormData(prev => {
                    const merged = { ...prev };
                    Object.entries(initialData).forEach(([key, val]) => {
                        const existing = prev[key];
                        const isEmpty = existing === undefined || existing === null || existing === '';
                        if (val !== undefined && val !== null && val !== '' && isEmpty) merged[key] = val;
                    });
                    return merged;
                });
            }
        }, [initialData]);

        const [familyMembers, setFamilyMembers] = useState<NomineeData[]>(initialNominees);
        const [errors, setErrors] = useState<ValidationErrors>({});
        const [familyErrors, setFamilyErrors] = useState<{ [id: string]: ValidationErrors }>({});
        const [acknowledgement, setAcknowledgement] = useState(false);
        const [acknowledgementB, setAcknowledgementB] = useState(false);
        const [sameAsPermAddress, setSameAsPermAddress] = useState(false);

        const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        useEffect(() => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(() => { onDraftSave?.(formData, familyMembers); }, 500);
            return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData, familyMembers]);

        // ── Auto-calculated ages from DOB ────────────────────────────────────
        const nomineeAge = useMemo(() => calculateAge(formData.nomineeDob), [formData.nomineeDob]);
        const familyAge = useMemo(() => calculateAge(formData.familyDob), [formData.familyDob]);

        const nomineeDobWasSet = useRef(!!toDateOrUndefined(formData.nomineeDob));
        const familyDobWasSet = useRef(!!toDateOrUndefined(formData.familyDob));

        useEffect(() => {
            if (nomineeAge !== null) {
                nomineeDobWasSet.current = true;
                setFormData(prev => ({ ...prev, nomineeAge: String(nomineeAge) }));
            } else if (nomineeDobWasSet.current) {
                nomineeDobWasSet.current = false;
                setFormData(prev => ({ ...prev, nomineeAge: '' }));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [nomineeAge]);

        useEffect(() => {
            if (familyAge !== null) {
                familyDobWasSet.current = true;
                setFormData(prev => ({ ...prev, familyAge: String(familyAge) }));
            } else if (familyDobWasSet.current) {
                familyDobWasSet.current = false;
                setFormData(prev => ({ ...prev, familyAge: '' }));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [familyAge]);

        const nomineeAddrTouched = useRef(false);
        const familyAddrTouched = useRef(false);

        useEffect(() => {
            if (formData.permanentAddress && !nomineeAddrTouched.current)
                setFormData(prev => ({ ...prev, nomineeAddress: prev.permanentAddress }));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData.permanentAddress]);

        useEffect(() => {
            if (formData.permanentAddress && !familyAddrTouched.current)
                setFormData(prev => ({ ...prev, familyAddress: prev.permanentAddress }));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData.permanentAddress]);

        // Sync temporary address when checkbox is on and permanent address changes
        useEffect(() => {
            if (sameAsPermAddress)
                setFormData(prev => ({ ...prev, temporaryAddress: prev.permanentAddress }));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData.permanentAddress, sameAsPermAddress]);

        const handleSameAsPermAddress = (checked: boolean) => {
            setSameAsPermAddress(checked);
            if (checked) {
                setFormData(prev => ({ ...prev, temporaryAddress: prev.permanentAddress }));
                if (errors.temporaryAddress) setErrors(prev => ({ ...prev, temporaryAddress: '' }));
            }
        };

        const genderOptions = [
            { label: 'Select', value: '' }, { label: 'Male', value: 'male' },
            { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' },
        ];
        const maritalStatusOptions = [
            { label: 'Select', value: '' }, { label: 'Unmarried', value: 'unmarried' },
            { label: 'Married', value: 'married' }, { label: 'Divorced', value: 'divorced' },
            { label: 'Widow', value: 'widow' }, { label: 'Widower', value: 'widower' },
        ];
        const epfRelationshipOptions = [
            { label: 'Select', value: '' }, { label: 'Spouse', value: 'spouse' },
            { label: 'Father', value: 'father' }, { label: 'Mother', value: 'mother' },
        ];
        const epsRelationshipOptions = [
            { label: 'Select', value: '' }, { label: 'Spouse', value: 'spouse' },
            { label: 'Father', value: 'father' }, { label: 'Mother', value: 'mother' },
            { label: 'Son', value: 'son' }, { label: 'Daughter', value: 'daughter' },
        ];

        const addFamilyMember = () =>
            setFamilyMembers(prev => prev.length >= 4 ? prev : [...prev, { id: Date.now().toString() }]);

        const removeFamilyMember = (id: string) => {
            setFamilyMembers(prev => prev.filter(n => n.id !== id));
            setFamilyErrors(prev => { const u = { ...prev }; delete u[id]; return u; });
        };

        const handleInputChange = (field: string, value: any) => {
            const nameFields = ['fullName', 'fatherName', 'nomineeName', 'familyMemberName', 'subscriberSignature', 'subscriberSignatureB', 'place'];
            const addressFields = ['permanentAddress', 'temporaryAddress', 'nomineeAddress', 'familyAddress'];
            if (typeof value === 'string') {
                if (addressFields.includes(field)) value = sanitiseAddress(value);
                else if (nameFields.includes(field)) value = allowOnlyAlphabets(value);
                else value = removeSpecialChars(value);
            }
            if (field === 'nomineeAddress') nomineeAddrTouched.current = true;
            if (field === 'familyAddress') familyAddrTouched.current = true;
            setFormData(prev => {
                const updated = { ...prev, [field]: value };
                // Auto-populate Part B signature from Part A if Part B is still empty
                if (field === 'subscriberSignature' && !prev.subscriberSignatureB) {
                    updated.subscriberSignatureB = value;
                }
                return updated;
            });
            if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        };

        const handleFamilyChange = (index: number, field: string, value: any) => {
            const nameFields = ['name', 'familyMemberName'];
            const addressFields = ['address', 'familyAddress'];
            if (typeof value === 'string') {
                if (addressFields.includes(field)) value = sanitiseAddress(value);
                else if (nameFields.includes(field)) value = allowOnlyAlphabets(value);
                else value = removeSpecialChars(value);
            }
            const updated = [...familyMembers];
            updated[index] = { ...updated[index], [field]: value };
            if (field === 'familyDob') {
                const age = calculateAge(value);
                updated[index].familyAge = age !== null ? String(age) : '';
            }
            setFamilyMembers(updated);
            const id = familyMembers[index].id;
            if (familyErrors[id]?.[field]) setFamilyErrors(prev => ({ ...prev, [id]: { ...prev[id], [field]: '' } }));
        };

        // ── Core validation — called by Continue button AND tab navigation ──
        const validateForm = (): boolean => {
            const e: ValidationErrors = {};

            if (!formData.oracleId) e.oracleId = 'Oracle ID is required';
            else if (!/^\d{7}$/.test(String(formData.oracleId)))
                e.oracleId = 'Oracle ID must be exactly 7 digits — e.g. 1234567';

            if (formData.uan && !/^\d{12}$/.test(String(formData.uan)))
                e.uan = 'UAN must be 12 digits — e.g. 123456789012';

            if (!formData.fullName) e.fullName = 'Full Name is required';
            if (!formData.fatherName) e.fatherName = "Father/Husband's Name is required";
            if (!formData.dob) e.dob = 'Date of Birth is required';
            if (!formData.gender) e.gender = 'Gender is required';
            if (!formData.maritalStatus) e.maritalStatus = 'Marital Status is required';
            if (!formData.joiningDate) e.joiningDate = 'Date of Joining is required';

            const addrCheck = (v: unknown, label: string, required = true): string => {
                const s = String(v ?? '').trim();
                if (required && s.length === 0) return `${label} is required`;
                if (s.length > 0 && s.length < 10) return `${label} must be at least 10 characters`;
                if (String(v ?? '').length > 200) return `${label} must not exceed 200 characters`;
                return '';
            };
            let err: string;
            err = addrCheck(formData.permanentAddress, 'Permanent Address'); if (err) e.permanentAddress = err;
            err = addrCheck(formData.temporaryAddress, 'Temporary Address'); if (err) e.temporaryAddress = err;
            err = addrCheck(formData.nomineeAddress, "Nominee's Address"); if (err) e.nomineeAddress = err;
            err = addrCheck(formData.familyAddress, 'Family Address'); if (err) e.familyAddress = err;

            if (!formData.nomineeName) e.nomineeName = "Nominee's Name is required";
            if (!formData.nomineeRelationship) e.nomineeRelationship = 'Nominee Relationship is required';

            const nomineeDobVal = toDateOrUndefined(formData.nomineeDob);
            const nomineeAgeVal = String(formData.nomineeAge ?? '').trim();
            if (!nomineeDobVal && !nomineeAgeVal) {
                e.nomineeDob = "Either Nominee's Date of Birth or Age is required";
            } else if (!nomineeDobVal && nomineeAgeVal) {
                const ageErr = ageCheck(nomineeAgeVal, "Nominee's Age");
                if (ageErr) e.nomineeDob = ageErr;
            }

            if (!formData.subscriberSignature) e.subscriberSignature = 'Subscriber Signature is required';

            if (!formData.familyMemberName) e.familyMemberName = 'Family Member Name is required';

            const familyDobVal = toDateOrUndefined(formData.familyDob);
            const familyAgeVal = String(formData.familyAge ?? '').trim();
            if (!familyDobVal && !familyAgeVal) {
                e.familyDob = 'Either Family Member Date of Birth or Age is required';
            } else if (!familyDobVal && familyAgeVal) {
                const ageErr = ageCheck(familyAgeVal, 'Family Member Age');
                if (ageErr) e.familyDob = ageErr;
            }

            if (!formData.familyRelationship) e.familyRelationship = 'Family Relationship is required';
            if (!formData.subscriberSignatureB) e.subscriberSignatureB = 'Part B Signature is required';
            if (!formData.place) e.place = 'Place is required';
            if (!acknowledgement) e.acknowledgement = 'Please confirm the Part A acknowledgement';
            if (!acknowledgementB) e.acknowledgementB = 'Please confirm the Part B acknowledgement';

            setErrors(e);

            const newFamilyErrors: { [id: string]: ValidationErrors } = {};
            let hasFamilyErrors = false;
            familyMembers.forEach((member, index) => {
                const fe: ValidationErrors = {};
                const lbl = `Family Member ${index + 2}`;
                if (!member.familyMemberName) { fe.familyMemberName = `${lbl} name is required`; hasFamilyErrors = true; }
                const mDobVal = toDateOrUndefined(member.familyDob);
                const mAgeVal = String(member.familyAge ?? '').trim();
                if (!mDobVal && !mAgeVal) {
                    fe.familyDob = `${lbl} DOB or age is required`;
                    hasFamilyErrors = true;
                } else if (!mDobVal && mAgeVal) {
                    const ageErr = ageCheck(mAgeVal, `${lbl} Age`);
                    if (ageErr) { fe.familyDob = ageErr; hasFamilyErrors = true; }
                }
                if (!member.familyRelationship) { fe.familyRelationship = `${lbl} relationship is required`; hasFamilyErrors = true; }
                const fa2Err = addrCheck(member.familyAddress, `${lbl} address`);
                if (fa2Err) { fe.familyAddress = fa2Err; hasFamilyErrors = true; }
                if (Object.keys(fe).length) newFamilyErrors[member.id] = fe;
            });
            setFamilyErrors(newFamilyErrors);

            const isValid = Object.keys(e).length === 0 && !hasFamilyErrors;
            if (!isValid) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Incomplete Form',
                    detail: 'Please fill in all mandatory fields before continuing.',
                    life: 4000,
                });
            }
            return isValid;
        };

        // ── Expose validate() to parent via ref ──────────────────────────────
        useImperativeHandle(ref, () => ({ validate: validateForm }));

        const handleContinue = () => {
            if (validateForm()) onContinue?.(formData, familyMembers);
            else window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return (
            <div className="form-container">
                <Toast ref={toast} position="top-right" />
                <h2 className="form-title">Form 2 Title</h2>

                {/* Row 1 */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Oracle ID*</label>
                        <InputText placeholder="Input" disabled value={formData.oracleId as string || ''} maxLength={7} className={errors.oracleId ? 'p-invalid' : ''} />
                        {errors.oracleId && <small className="p-error">{errors.oracleId}</small>}
                    </div>
                    <div className="form-field">
                        <label>UAN</label>
                        <InputText
                            placeholder="12-digit number — e.g. 123456789012"
                            value={formData.uan as string || ''}
                            onChange={(e) => handleInputChange('uan', e.target.value.replace(/\D/g, '').slice(0, 12))}
                            maxLength={12}
                            className={errors.uan ? 'p-invalid' : ''}
                        />
                        {errors.uan && <small className="p-error">{errors.uan}</small>}
                    </div>
                    <div className="form-field">
                        <label>Full Name*</label>
                        <InputText placeholder="Input" value={formData.fullName as string || ''} onChange={(e) => handleInputChange('fullName', e.target.value)} maxLength={100} className={errors.fullName ? 'p-invalid' : ''} />
                        {errors.fullName && <small className="p-error">{errors.fullName}</small>}
                    </div>
                    <div className="form-field">
                        <label>Father/Husband's Name*</label>
                        <InputText placeholder="Input" value={formData.fatherName as string || ''} onChange={(e) => handleInputChange('fatherName', e.target.value)} maxLength={100} className={errors.fatherName ? 'p-invalid' : ''} />
                        {errors.fatherName && <small className="p-error">{errors.fatherName}</small>}
                    </div>
                    <div className="form-field">
                        <label>Date of Birth* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.dob)} onChange={(e) => handleInputChange('dob', e.value)} placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY} className={errors.dob ? 'p-invalid' : ''} />
                        {errors.dob && <small className="p-error">{errors.dob}</small>}
                    </div>
                </div>

                {/* Row 2 */}
                <div className="form-row white-bg">
                    <div className="form-field">
                        <label>Gender*</label>
                        <Dropdown value={formData.gender} options={genderOptions} onChange={(e) => handleInputChange('gender', e.value)} placeholder="Select" className={errors.gender ? 'p-invalid' : ''} />
                        {errors.gender && <small className="p-error">{errors.gender}</small>}
                    </div>
                    <div className="form-field">
                        <label>Marital Status*</label>
                        <Dropdown value={formData.maritalStatus} options={maritalStatusOptions} onChange={(e) => handleInputChange('maritalStatus', e.value)} placeholder="Select" className={errors.maritalStatus ? 'p-invalid' : ''} />
                        {errors.maritalStatus && <small className="p-error">{errors.maritalStatus}</small>}
                    </div>
                    <div className="form-field">
                        <label>PF Account No.</label>
                        <InputText placeholder="" disabled value={formData.pfAccount as string || ''} />
                    </div>
                    <div className="form-field">
                        <label>Date of Joining* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.joiningDate)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput className={errors.joiningDate ? 'p-invalid' : ''} />
                        {errors.joiningDate && <small className="p-error">{errors.joiningDate}</small>}
                    </div>
                </div>

                {/* Row 3 — Addresses */}
                <div className="form-row green-bg two-column">
                    <div className="form-field full-width">
                        <label>Permanent Address*</label>
                        <InputText placeholder="e.g. Flat 4-B, Sector-62, Noida - 201301" maxLength={200} value={formData.permanentAddress as string || ''} onChange={(e) => handleInputChange('permanentAddress', e.target.value)} className={errors.permanentAddress ? 'p-invalid' : ''} />
                        <small className="char-counter">{addrRemaining(formData.permanentAddress as string)}</small>
                        {errors.permanentAddress && <small className="p-error">{errors.permanentAddress}</small>}
                    </div>
                    <div className="form-field full-width">
                        <label>
                            Temporary Address*
                            {sameAsPermAddress && <span className="address-copy-badge">copied from permanent</span>}
                        </label>
                        <InputText
                            placeholder="e.g. H.No. 1/234, MG Road, Mumbai - 400001"
                            maxLength={200}
                            value={formData.temporaryAddress as string || ''}
                            onChange={(e) => handleInputChange('temporaryAddress', e.target.value)}
                            disabled={sameAsPermAddress}
                            className={errors.temporaryAddress ? 'p-invalid' : ''}
                        />
                        <small className="char-counter">{addrRemaining(formData.temporaryAddress as string)}</small>
                        {errors.temporaryAddress && <small className="p-error">{errors.temporaryAddress}</small>}
                    </div>
                </div>
                <div className="same-address-checkbox-row">
                    <input
                        type="checkbox"
                        id="sameAsPermAddress"
                        className="ack-checkbox"
                        checked={sameAsPermAddress}
                        onChange={(e) => handleSameAsPermAddress(e.target.checked)}
                    />
                    <label htmlFor="sameAsPermAddress" className="same-address-label">
                        Temporary address is same as Permanent address
                    </label>
                </div>

                {/* ═══ PART A — EPF ═══ */}
                <div className="section-block">
                    <div className="section-header-bar"><span className="section-header-label">Part A — EPF Nominee (Primary)</span></div>
                    <p className="nominee-instruction">I hereby nominate the primary person / cancel the nomination made by me previously, and nominate the person mentioned below to receive the amount standing to my credit in the Employee's Provident Fund, in the event of my death.</p>

                    <div className="form-row white-bg two-column">
                        <div className="form-field full-width">
                            <label>Name of Nominee*</label>
                            <InputText placeholder="Input" value={formData.nomineeName as string || ''} onChange={(e) => handleInputChange('nomineeName', e.target.value)} maxLength={100} className={errors.nomineeName ? 'p-invalid' : ''} />
                            {errors.nomineeName && <small className="p-error">{errors.nomineeName}</small>}
                        </div>
                        <div className="form-field full-width">
                            <label>Address of Nominee*</label>
                            <InputText placeholder="Auto-populated from Permanent Address" maxLength={200} value={formData.nomineeAddress as string || ''} onChange={(e) => handleInputChange('nomineeAddress', e.target.value)} className={errors.nomineeAddress ? 'p-invalid' : ''} />
                            <small className="char-counter">{addrRemaining(formData.nomineeAddress as string)}</small>
                            {errors.nomineeAddress && <small className="p-error">{errors.nomineeAddress}</small>}
                        </div>
                    </div>

                    <div className="form-row green-bg">
                        <div className="form-field">
                            <label>Nominee Relationship*</label>
                            <Dropdown value={formData.nomineeRelationship} options={epfRelationshipOptions} onChange={(e) => handleInputChange('nomineeRelationship', e.value)} placeholder="Select" className={errors.nomineeRelationship ? 'p-invalid' : ''} />
                            {errors.nomineeRelationship && <small className="p-error">{errors.nomineeRelationship}</small>}
                        </div>
                        <div className="form-field">
                            <label>Date of Birth of Nominee <span className="date-hint">(DD/MM/YYYY)</span></label>
                            <ClearableCalendar
                                value={toDateOrUndefined(formData.nomineeDob)}
                                onChange={(val) => handleInputChange('nomineeDob', val)}
                                placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY}
                                className={errors.nomineeDob ? 'p-invalid' : ''}
                            />
                            {errors.nomineeDob && <small className="p-error">{errors.nomineeDob}</small>}
                        </div>
                        <div className="form-field">
                            <label>Nominee Age</label>
                            <InputText
                                value={nomineeAge !== null ? String(nomineeAge) : (formData.nomineeAge as string || '')}
                                onChange={(e) => handleInputChange('nomineeAge', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder={nomineeAge !== null ? `${nomineeAge} (auto)` : 'Enter age'}
                                disabled={nomineeAge !== null}
                                maxLength={3}
                            />
                            <small className="field-hint">Auto-calculated if DOB is entered</small>
                        </div>
                        <div className="form-field">
                            <label>Proportion*{' '}
                                <i className="pi pi-info-circle proportion-tooltip" data-pr-tooltip="Total amount of share of accumulation in Provident Fund to be paid to each nominee" data-pr-position="top" />
                            </label>
                            <Tooltip target=".proportion-tooltip" />
                            <InputText placeholder="Fixed at 100%" value="100%" disabled maxLength={3} />
                        </div>
                    </div>

                    <div className="statutory-notes">
                        <p>1. * Certified that I have no family as defined in para 2(g) of the Employee's Provident Fund Scheme, 1952 and should I acquire a family hereafter the above nomination should be deemed as cancelled.</p>
                        <p>2. * Certified that my father/mother is/are dependent upon me.</p>
                    </div>

                    <div className="form-row white-bg two-column" style={{ margin: '0 8px' }}>
                        <div className="form-field full-width">
                            <label>Signature of the Subscriber*</label>
                            <SignatureInput value={formData.subscriberSignature as string || ''} onChange={(v) => handleInputChange('subscriberSignature', v)} placeholder="Type your full name" className={errors.subscriberSignature ? 'p-invalid' : ''} />
                            {errors.subscriberSignature && <small className="p-error">{errors.subscriberSignature}</small>}
                        </div>
                        <div className="form-field full-width">
                            <label>Signature of the Employer</label>
                            <InputText disabled value={formData.employerSignature as string || ''} placeholder="To be filled by employer" />
                        </div>
                    </div>

                    <div className="acknowledgement-section" style={{ margin: '8px 8px 12px' }}>
                        <div className={`acknowledgement-checkbox-row ${errors.acknowledgement ? 'has-error' : ''}`}>
                            <input type="checkbox" id="ack-partA" className="ack-checkbox" checked={acknowledgement}
                                onChange={(e) => { setAcknowledgement(e.target.checked); if (errors.acknowledgement) setErrors(prev => ({ ...prev, acknowledgement: '' })); }}
                                disabled={!formData.subscriberSignature} />
                            <span className="acknowledgement-label" onClick={() => { if (formData.subscriberSignature) setAcknowledgement(!acknowledgement); }}>
                                I hereby certify that the digital signature provided is true and the information furnished is accurate to the best of my knowledge.
                            </span>
                        </div>
                        {!formData.subscriberSignature && <small className="field-hint">Please enter your signature above to enable this checkbox</small>}
                        {errors.acknowledgement && <small className="p-error">{errors.acknowledgement}</small>}
                    </div>
                </div>

                {/* ═══ PART B — EPS ═══ */}
                <div className="section-block">
                    <div className="section-header-bar eps-header"><span className="section-header-label">Part B — EPS Family Members</span></div>
                    <p className="nominee-instruction">I hereby furnish below particulars of the members of my family who would be eligible to receive widow / children pension in the event of my death.</p>

                    <div className="form-row green-bg">
                        <div className="form-field">
                            <label>Name of the Family Member*</label>
                            <InputText placeholder="Input" value={formData.familyMemberName as string || ''} onChange={(e) => handleInputChange('familyMemberName', e.target.value)} maxLength={100} className={errors.familyMemberName ? 'p-invalid' : ''} />
                            {errors.familyMemberName && <small className="p-error">{errors.familyMemberName}</small>}
                        </div>
                        <div className="form-field full-width">
                            <label>Family Address*</label>
                            <InputText placeholder="Auto-populated from Permanent Address" maxLength={200} value={formData.familyAddress as string || ''} onChange={(e) => handleInputChange('familyAddress', e.target.value)} className={errors.familyAddress ? 'p-invalid' : ''} />
                            <small className="char-counter">{addrRemaining(formData.familyAddress as string)}</small>
                            {errors.familyAddress && <small className="p-error">{errors.familyAddress}</small>}
                        </div>
                        <div className="form-field">
                            <label>Date Of Birth <span className="date-hint">(DD/MM/YYYY)</span></label>
                            <ClearableCalendar
                                value={toDateOrUndefined(formData.familyDob)}
                                onChange={(val) => handleInputChange('familyDob', val)}
                                placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY}
                                className={errors.familyDob ? 'p-invalid' : ''}
                            />
                            {errors.familyDob && <small className="p-error">{errors.familyDob}</small>}
                        </div>
                        <div className="form-field">
                            <label>Age</label>
                            <InputText
                                value={familyAge !== null ? String(familyAge) : (formData.familyAge as string || '')}
                                onChange={(e) => handleInputChange('familyAge', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder={familyAge !== null ? `${familyAge} (auto)` : 'Enter age'}
                                disabled={familyAge !== null}
                                maxLength={3}
                            />
                        </div>
                        <div className="form-field">
                            <label>Relationship*</label>
                            <Dropdown value={formData.familyRelationship} options={epsRelationshipOptions} onChange={(e) => handleInputChange('familyRelationship', e.value)} placeholder="Select" className={errors.familyRelationship ? 'p-invalid' : ''} />
                            {errors.familyRelationship && <small className="p-error">{errors.familyRelationship}</small>}
                        </div>
                    </div>

                    <Button label="Add Family Member" icon="pi pi-plus" onClick={addFamilyMember} className="add-nominee-btn p-button-primary" disabled={familyMembers.length >= 4} />

                    {familyMembers.map((member, index) => {
                        const fe = familyErrors[member.id] || {};
                        const fAgeDisplay = member.familyAge || '';
                        const fDobPresent = !!member.familyDob;
                        return (
                            <div key={member.id} className="dynamic-nominee">
                                <button className="remove-nominee-btn" onClick={() => removeFamilyMember(member.id)} title="Remove">×</button>
                                {Object.keys(fe).length > 0 && (
                                    <div className="nominee-error-banner">Please complete all required fields for Family Member {index + 2} or remove</div>
                                )}
                                <div className="form-row white-bg">
                                    <div className="form-field">
                                        <label>Family Member {index + 2}*</label>
                                        <InputText placeholder="Input" value={member.familyMemberName || ''} onChange={(e) => handleFamilyChange(index, 'familyMemberName', e.target.value)} maxLength={100} className={fe.familyMemberName ? 'p-invalid' : ''} />
                                        {fe.familyMemberName && <small className="p-error">{fe.familyMemberName}</small>}
                                    </div>
                                    <div className="form-field full-width">
                                        <label>Family Address {index + 2}*</label>
                                        <InputText placeholder="Address" maxLength={200} value={member.familyAddress || ''} onChange={(e) => handleFamilyChange(index, 'familyAddress', e.target.value)} className={fe.familyAddress ? 'p-invalid' : ''} />
                                        {fe.familyAddress && <small className="p-error">{fe.familyAddress}</small>}
                                    </div>
                                    <div className="form-field">
                                        <label>DOB {index + 2} <span className="date-hint">(DD/MM/YYYY)</span></label>
                                        <ClearableCalendar
                                            value={toDateOrUndefined(member.familyDob)}
                                            onChange={(val) => handleFamilyChange(index, 'familyDob', val)}
                                            placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY}
                                            className={fe.familyDob ? 'p-invalid' : ''}
                                        />
                                        {fe.familyDob && <small className="p-error">{fe.familyDob}</small>}
                                    </div>
                                    <div className="form-field">
                                        <label>Age {index + 2}</label>
                                        <InputText
                                            value={fAgeDisplay}
                                            onChange={(e) => handleFamilyChange(index, 'familyAge', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                            placeholder={fDobPresent ? `${fAgeDisplay} (auto)` : 'Enter age'}
                                            disabled={fDobPresent}
                                            maxLength={3}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label>Relationship {index + 2}*</label>
                                        <Dropdown value={member.familyRelationship} options={epsRelationshipOptions} onChange={(e) => handleFamilyChange(index, 'familyRelationship', e.value)} placeholder="Select" className={fe.familyRelationship ? 'p-invalid' : ''} />
                                        {fe.familyRelationship && <small className="p-error">{fe.familyRelationship}</small>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="statutory-notes">
                        <p>** Certified that I have no family as defined in para 2(b) of the Employee's Pension Scheme, 1995 and should I acquire a family hereafter I shall furnish particulars thereon in the above form.</p>
                        <p>I hereby nominate the following person for receiving the monthly widow pension (admissible under para 16 2(a) (I) & (ii)) in the event of the death without leaving any eligible family member for receiving pension.</p>
                    </div>

                    <div className="form-row white-bg two-column" style={{ margin: '0 8px' }}>
                        <div className="form-field full-width">
                            <label>Signature of the Subscriber*</label>
                            <SignatureInput value={formData.subscriberSignatureB as string || ''} onChange={(v) => handleInputChange('subscriberSignatureB', v)} placeholder="Type your full name" className={errors.subscriberSignatureB ? 'p-invalid' : ''} />
                            {errors.subscriberSignatureB && <small className="p-error">{errors.subscriberSignatureB}</small>}
                        </div>
                        <div className="form-field full-width">
                            <label>Signature of the Employer</label>
                            <InputText disabled value={formData.employerSignature as string || ''} placeholder="To be filled by employer" />
                        </div>
                    </div>

                    <div className="acknowledgement-section" style={{ margin: '8px 8px 12px' }}>
                        <div className={`acknowledgement-checkbox-row ${errors.acknowledgementB ? 'has-error' : ''}`}>
                            <input type="checkbox" id="ack-partB" className="ack-checkbox" checked={acknowledgementB}
                                onChange={(e) => { setAcknowledgementB(e.target.checked); if (errors.acknowledgementB) setErrors(prev => ({ ...prev, acknowledgementB: '' })); }}
                                disabled={!formData.subscriberSignatureB} />
                            <span className="acknowledgement-label" onClick={() => { if (formData.subscriberSignatureB) setAcknowledgementB(!acknowledgementB); }}>
                                I hereby certify that the digital signature provided is true and the information furnished is accurate to the best of my knowledge.
                            </span>
                        </div>
                        {!formData.subscriberSignatureB && <small className="field-hint">Please enter your signature above to enable this checkbox</small>}
                        {errors.acknowledgementB && <small className="p-error">{errors.acknowledgementB}</small>}
                    </div>
                </div>

                {/* Place & Date */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Place*</label>
                        <InputText disabled value={formData.place as string || ''} placeholder="Auto populated" className={errors.place ? 'p-invalid' : ''} />
                        {errors.place && <small className="p-error">{errors.place}</small>}
                    </div>
                    <div className="form-field">
                        <label>Date <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.date)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                </div>

                <div className="form-actions">
                    <Button label="Continue" onClick={handleContinue} className="continue-btn p-button-primary" />
                </div>
            </div>
        );
    }
);

Form2.displayName = 'Form2';
export default Form2;
